// One file's own page: what it is, what was taken from it, who can see it.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiFailure, type ApiClient } from '../api/client.js';
import type { FileView } from '../api/contract.js';
import { dayOnly } from '../format.js';
import { viewerIsPrincipal } from '../plan/snapshot.js';
import type { PlanState } from '../plan/usePlan.js';
import { fileProblem } from './AddFile.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { FileEditPanel } from './FileMenu.js';
import { stateWords, uploaderLabel, visibilityLabel } from './FileRows.js';

export type FilePageProps = {
  state: PlanState;
  client: ApiClient;
  planId: string;
  fileId: string;
};

export function FilePage({
  state,
  client,
  planId,
  fileId,
}: FilePageProps): JSX.Element | null {
  const snapshot = state.snapshot;
  const navigate = useNavigate();
  const [file, setFile] = useState<FileView | null>(null);
  const [missing, setMissing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [mode, setMode] = useState<'view' | 'change' | 'delete'>('view');
  const [version, setVersion] = useState(0);
  const reload = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    let current = true;
    client.getFile(planId, fileId).then(
      (loaded) => {
        if (current) {
          setFile(loaded);
          setMissing(false);
        }
      },
      (error: unknown) => {
        if (!current) {
          return;
        }
        if (error instanceof ApiFailure && error.status === 404) {
          setMissing(true);
          setFile(null);
        } else {
          setProblem('The file didn’t load. Try again.');
        }
      },
    );
    return () => {
      current = false;
    };
  }, [client, planId, fileId, snapshot, version]);

  if (snapshot === null) {
    return null;
  }
  const filesPath = `/plans/${planId}/files`;
  if (missing) {
    return (
      <section className="filesPage">
        <p className="filesPage__empty">This file isn’t available to you.</p>
      </section>
    );
  }
  if (file === null) {
    return problem === null ? null : (
      <section className="filesPage">
        <p className="page__problem" role="alert">
          {problem}
        </p>
      </section>
    );
  }
  const principal = viewerIsPrincipal(snapshot);
  const words = stateWords(file);

  return (
    <section className="filesPage">
      <h1 className="page__title">{file.name}</h1>
      <p className="page__subtitle">
        {uploaderLabel(file, snapshot.people)} on {dayOnly(file.createdAt)}
      </p>
      <h2 className="filePage__heading">Summary</h2>
      <p className="filePage__summary">
        {file.state === 'ready' ? (file.description ?? '') : words}
      </p>
      <h2 className="filePage__heading">Who can see it</h2>
      {mode === 'change' ? (
        <div className="fileCard">
          <FileEditPanel
            client={client}
            planId={planId}
            file={file}
            people={snapshot.people}
            viewerIsPrincipal={principal}
            onCancel={() => setMode('view')}
            onDone={() => {
              setMode('view');
              reload();
            }}
          />
        </div>
      ) : (
        <p className="filePage__who">
          <span>{visibilityLabel(file, snapshot.people)}</span>
          {file.canChange ? (
            <button
              type="button"
              className="artifact__link"
              onClick={() => setMode('change')}
            >
              Change
            </button>
          ) : null}
        </p>
      )}
      <div className="fileForm__buttons fileForm__buttons--start">
        <button
          type="button"
          className="button"
          onClick={() => {
            setProblem(null);
            client.downloadFileOriginal(planId, file.id, file.name).catch(() => {
              setProblem('That didn’t download. Try again.');
            });
          }}
        >
          Download
        </button>
        {file.canChange ? (
          <button type="button" className="button" onClick={() => setMode('delete')}>
            Delete
          </button>
        ) : null}
      </div>
      {problem === null ? null : (
        <p className="artifact__problem" role="alert">
          {problem}
        </p>
      )}
      {mode === 'delete' ? (
        <ConfirmDialog
          title="Delete this file?"
          name={file.name}
          detail="The contents of this file will no longer be available to Mor."
          confirmLabel="Delete"
          problemFor={fileProblem}
          onConfirm={async () => {
            await client.deleteFile(planId, file.id);
            navigate(filesPath);
          }}
          onCancel={() => setMode('view')}
        />
      ) : null}
    </section>
  );
}
